import { useRef, useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import axios from '../api/axios';
import '../index.css';

const RESET_URL = '/auth/reset-password';
const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).{8,72}$/;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const pwdRef = useRef();
  const errRef = useRef();

  const [pwd, setPwd] = useState('');
  const [matchPwd, setMatchPwd] = useState('');
  const [validPwd, setValidPwd] = useState(false);
  const [validMatch, setValidMatch] = useState(false);
  const [pwdFocus, setPwdFocus] = useState(false);
  const [matchFocus, setMatchFocus] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setErrMsg('Reset link is missing or invalid. Request a new one.');
    } else {
      pwdRef.current?.focus();
    }
  }, [token]);

  useEffect(() => {
    setValidPwd(PWD_REGEX.test(pwd));
    setValidMatch(pwd === matchPwd && pwd.length > 0);
  }, [pwd, matchPwd]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrMsg('');
    setSuccessMsg('');

    if (!token) {
      setErrMsg('Reset link is missing or invalid.');
      errRef.current.focus();
      return;
    }

    if (!validPwd || !validMatch) {
      setErrMsg('Enter a valid matching password.');
      errRef.current.focus();
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        RESET_URL,
        JSON.stringify({ token, pwd }),
        { headers: { 'Content-Type': 'application/json' }, withCredentials: true }
      );
      setSuccessMsg(response?.data?.message || 'Password updated successfully.');
      setPwd('');
      setMatchPwd('');
      setTimeout(() => navigate('/login', { replace: true }), 1800);
    } catch (err) {
      if (!err?.response) {
        setErrMsg('No server response. Try again later.');
      } else {
        setErrMsg(err.response?.data?.message || 'Could not reset password.');
      }
      errRef.current.focus();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="btn" className="App">
      <section>
        <p ref={errRef} className={errMsg ? 'errmsg' : 'offscreen'} aria-live="assertive">{errMsg}</p>
        <p className={successMsg ? 'line' : 'offscreen'} aria-live="polite">{successMsg}</p>

        <h1>Reset password</h1>
        <p className="line">Choose a new password for your Recipe Lab account.</p>

        <form onSubmit={handleSubmit}>
          <label htmlFor="password">
            New password
            <FontAwesomeIcon icon={faCheck} className={validPwd ? 'valid' : 'hide'} />
            <FontAwesomeIcon icon={faTimes} className={validPwd || !pwd ? 'hide' : 'invalid'} />
          </label>
          <input
            type="password"
            id="password"
            ref={pwdRef}
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            required
            aria-invalid={validPwd ? 'false' : 'true'}
            onFocus={() => setPwdFocus(true)}
            onBlur={() => setPwdFocus(false)}
          />
          <p id="pwdnote" className={pwdFocus && !validPwd ? 'instructions' : 'offscreen'}>
            <FontAwesomeIcon icon={faInfoCircle} />
            At least 8 characters with uppercase, lowercase, and a number.
          </p>

          <label htmlFor="confirm_pwd">
            Confirm password
            <FontAwesomeIcon icon={faCheck} className={validMatch && matchPwd ? 'valid' : 'hide'} />
            <FontAwesomeIcon icon={faTimes} className={validMatch || !matchPwd ? 'hide' : 'invalid'} />
          </label>
          <input
            type="password"
            id="confirm_pwd"
            value={matchPwd}
            onChange={(e) => setMatchPwd(e.target.value)}
            required
            aria-invalid={validMatch ? 'false' : 'true'}
            onFocus={() => setMatchFocus(true)}
            onBlur={() => setMatchFocus(false)}
          />
          <p id="confirmnote" className={matchFocus && !validMatch ? 'instructions' : 'offscreen'}>
            <FontAwesomeIcon icon={faInfoCircle} />
            Must match the new password.
          </p>

          <button className="button" type="submit" disabled={!validPwd || !validMatch || !token || isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Update password'}
          </button>
        </form>

        <p>
          <Link to="/forgot-password">Request a new reset link</Link>
        </p>
        <p>
          <Link to="/login">Back to sign in</Link>
        </p>
      </section>
    </div>
  );
};

export default ResetPassword;